import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useUpdateSubscription, type PlatformCompanySummary } from "@/hooks/usePlatformSummary";
import { toast } from "sonner";

interface Props {
  company: PlatformCompanySummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(v);

const EditSubscriptionDialog = ({ company, open, onOpenChange }: Props) => {
  const [planTier, setPlanTier] = useState("base");
  const [status, setStatus] = useState("active");
  const [monthlyRate, setMonthlyRate] = useState("500");
  const [seatCount, setSeatCount] = useState("5");
  const [includedSeats, setIncludedSeats] = useState("3");
  const [pricePerSeat, setPricePerSeat] = useState("49");
  const [includedContractors, setIncludedContractors] = useState("10");
  const [contractorBlockSize, setContractorBlockSize] = useState("10");
  const [contractorBlockPrice, setContractorBlockPrice] = useState("9");
  const [pricingNote, setPricingNote] = useState("");
  const mutation = useUpdateSubscription();

  useEffect(() => {
    if (company) {
      setPlanTier(company.plan_tier ?? "base");
      setStatus(company.subscription_status ?? "active");
      setMonthlyRate(String(company.monthly_rate ?? 500));
      setSeatCount(String(company.seat_count ?? 5));
      setIncludedSeats(String(company.included_seats ?? 3));
      setPricePerSeat(String(company.price_per_additional_seat ?? 49));
      setIncludedContractors(String(company.included_contractors ?? 10));
      setContractorBlockSize(String(company.contractor_block_size ?? 10));
      setContractorBlockPrice(String(company.contractor_block_price ?? 9));
      setPricingNote("");
    }
  }, [company]);

  /* Live MRR preview using actual paid_seat_count & contractor_count from RPC */
  const preview = useMemo(() => {
    if (!company) return null;
    const base = Number(monthlyRate) || 0;
    const inclSeats = Number(includedSeats) || 3;
    const perSeat = Number(pricePerSeat) || 49;
    const inclContr = Number(includedContractors) || 10;
    const blkSize = Number(contractorBlockSize) || 10;
    const blkPrice = Number(contractorBlockPrice) || 9;

    const seatOverage = Math.max(0, company.paid_seat_count - inclSeats) * perSeat;
    const contrBlocks = Math.ceil(Math.max(0, company.contractor_count - inclContr) / blkSize);
    const contrOverage = contrBlocks * blkPrice;
    const total = base + seatOverage + contrOverage;

    return { base, seatOverage, contrOverage, total, seatOver: Math.max(0, company.paid_seat_count - inclSeats), contrBlocks };
  }, [company, monthlyRate, includedSeats, pricePerSeat, includedContractors, contractorBlockSize, contractorBlockPrice]);

  const handleSave = async () => {
    if (!company) return;
    try {
      await mutation.mutateAsync({
        companyId: company.company_id,
        planTier,
        status,
        monthlyRate: Number(monthlyRate) || 0,
        seatCount: Math.max(1, Number(seatCount) || 0),
        includedSeats: Number(includedSeats) || 0,
        pricePerAdditionalSeat: Number(pricePerSeat) || 0,
        includedContractors: Number(includedContractors) || 0,
        contractorBlockSize: Math.max(1, Number(contractorBlockSize) || 0),
        contractorBlockPrice: Number(contractorBlockPrice) || 0,
        pricingNote: pricingNote || null,
      });
      toast.success("Subscription updated");
      onOpenChange(false);
    } catch {
      toast.error("Failed to update subscription");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Subscription — {company?.company_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Plan Tier</Label>
              <Select value={planTier} onValueChange={setPlanTier}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="base">Base — $500/mo base</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="past_due">Past Due</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Base Monthly Rate ($)</Label>
              <Input type="number" value={monthlyRate} onChange={(e) => setMonthlyRate(e.target.value)} min={0} />
            </div>
            <div className="space-y-1.5">
              <Label>Total Seat Count</Label>
              <Input type="number" value={seatCount} onChange={(e) => setSeatCount(e.target.value)} min={1} />
            </div>
          </div>

          <Separator />
          <p className="text-xs font-medium text-muted-foreground">Pricing Parameters</p>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Included Paid Seats</Label>
              <Input type="number" value={includedSeats} onChange={(e) => setIncludedSeats(e.target.value)} min={0} />
            </div>
            <div className="space-y-1.5">
              <Label>Price per Additional Seat ($)</Label>
              <Input type="number" value={pricePerSeat} onChange={(e) => setPricePerSeat(e.target.value)} min={0} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Included Contractors</Label>
              <Input type="number" value={includedContractors} onChange={(e) => setIncludedContractors(e.target.value)} min={0} />
            </div>
            <div className="space-y-1.5">
              <Label>Block Size</Label>
              <Input type="number" value={contractorBlockSize} onChange={(e) => setContractorBlockSize(e.target.value)} min={1} />
            </div>
            <div className="space-y-1.5">
              <Label>Block Price ($)</Label>
              <Input type="number" value={contractorBlockPrice} onChange={(e) => setContractorBlockPrice(e.target.value)} min={0} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Pricing Note</Label>
            <Input value={pricingNote} onChange={(e) => setPricingNote(e.target.value)} placeholder="Optional note…" />
          </div>

          {/* Live MRR Preview */}
          {preview && (
            <>
              <Separator />
              <div className="bg-muted/50 rounded-md p-3 text-sm font-mono space-y-1">
                <div className="flex justify-between"><span>Base price:</span><span>{fmt(preview.base)}</span></div>
                <div className="flex justify-between">
                  <span>Seat overage ({preview.seatOver} × ${pricePerSeat}):</span>
                  <span>{fmt(preview.seatOverage)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Contractor overage ({preview.contrBlocks} blocks):</span>
                  <span>{fmt(preview.contrOverage)}</span>
                </div>
                <Separator className="my-1" />
                <div className="flex justify-between font-bold">
                  <span>Calculated MRR:</span>
                  <span>{fmt(preview.total)}/mo</span>
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={mutation.isPending}>
              {mutation.isPending ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditSubscriptionDialog;
